import { compact, get, isEmpty, map, reduce, round, uniq } from 'lodash';
import moment from 'moment';
import { Contact, Currency, HBL, Shipment, ShipmentContainer } from './type';
import Decimal from 'decimal.js';
import React from 'react';
import { getValuesByKey } from '@/utils/shipment';
import { request } from 'umi';

export function renderLedgerLink(record: any) {
  let entityType;
  let entity;
  let text = record.ref_no;
  if (record.journal_entry) {
    entityType = 'journal_entry';
    entity = record.journal_entry;
  } else {
    for (const key of ['invoice', 'bill', 'dc_note', 'transaction', 'shipment']) {
      if (
        text &&
        record[key] &&
        record[key][key === 'shipment' ? 'shipment_number' : 'code'] === text
      ) {
        entityType = key;
        entity = record[key];
        break;
      }
      if (!text && record[key] && !record[key].code) {
        if (
          (key === 'bill' && ['expense', 'payable'].includes(record.target || '')) ||
          (key === 'transaction' && ['cash', 'transaction'].includes(record.target || ''))
        ) {
          entityType = key;
          entity = record[key];
          break;
        }
      }
    }
  }
  if (entityType && entity) {
    let path = '';
    if (entityType === 'dc_note') {
      path = `/${entity.type}_notes`;
    } else if (entityType === 'invoice') {
      path = `/invoices`;
    } else if (entityType === 'bill') {
      path = `/bills`;
    } else if (entityType === 'transaction') {
      path = `/transactions/${entity.direction === 'incoming' ? 'incoming_' : ''}payments`;
    } else if (entityType === 'journal_entry') {
      path = `/journals`;
      if (!text) {
        text = `${entity.post_date}: ${entity.date_seq}`;
      }
    } else if (entityType === 'shipment' && entity.type) {
      return {
        text: text || '-',
        href: `/shipments/${entity.type}/${entity.ext_id}`,
      };
    }
    return {
      text: text || '-',
      href: `/accounting${path}/${entity.ext_id}`,
    };
  }
  return {
    text: text || '-',
  };
}

export function lbsToKg(pounds: number) {
  return round(pounds / 2.20462, 6);
}
export function kgToLbs(kg: number) {
  return round(kg * 2.20462, 6);
}

export function cftToCbm(cubicFeet: number) {
  return round(cubicFeet / 35.3147, 6);
}

export function cbmToCft(cbm: number) {
  return round(cbm * 35.3147, 6);
}

type CarrierSite = {
  // Where the "Carrier Website" menu item lands. Carriers that accept the B/L
  // in the query string get a deep link; for the rest we can only open their
  // tracking page and the user still has to paste the number (see below).
  url: (mbl_no: string, bl_no: string) => string;
  // A few carriers only accept the number through a form submission, so we
  // build one on the fly and submit it instead of opening the URL directly.
  form?: {
    method: 'GET' | 'POST';
    fields: (mbl_no: string, bl_no: string) => Record<string, string>;
  };
};

// Keyed by the SCAC, i.e. the first 4 characters of the MBL number.
// NOTE: MEDU (MSC), WHLC (Wan Hai) and OOLU (OOCL) have no working deep link —
// their tracking is an in-page AJAX search that ignores query parameters, and
// their legacy entry points are now behind a bot check (OOCL's
// moc.oocl.com/.../ct_search_from_other_sys.jsf returns 403, Wan Hai serves an
// Imperva CAPTCHA). We open their tracking page so the user only has to paste.
const CARRIER_SITES: Record<string, CarrierSite> = {
  HLCU: {
    url: mbl_no =>
      `https://www.hapag-lloyd.com/en/online-business/track/track-by-booking-solution.html?blno=${mbl_no}`,
  },
  HDMU: {
    url: () => `https://www.hmm21.com/e-service/search/index.do`,
    form: { method: 'GET', fields: mbl_no => ({ query: mbl_no }) },
  },
  // Hede Shipping (合德海运). The portal posts the B/L to getVBilldynamicEN and
  // needs no session, but it is HTTP only (port 443 is closed), so a browser on
  // an HTTPS page warns before submitting. There is no HTTPS endpoint to use.
  HDUJ: {
    url: () => `http://elines.hedehk.com/getVBilldynamicEN`,
    form: { method: 'POST', fields: (mbl_no, bl_no) => ({ billno: bl_no }) },
  },
  CMDU: {
    url: () => `https://www.cma-cgm.com/ebusiness/tracking/search`,
    form: {
      method: 'POST',
      fields: (mbl_no, bl_no) => ({
        'SearchViewModel.Reference': bl_no,
        'SearchViewModel.FromHome': 'true',
        'SearchViewModel.SearchBy': 'Container',
        'g-recaptcha-response': '',
      }),
    },
  },
  WHLC: {
    url: () => `https://www.wanhai.com/views/cargoTrack/CargoTrack.xhtml`,
  },
  MAEU: {
    url: (mbl_no, bl_no) => `https://www.maersk.com/tracking/${bl_no}`,
  },
  COSU: {
    url: (mbl_no, bl_no) =>
      `https://elines.coscoshipping.com/ebusiness/cargoTracking?trackingType=BOOKING&number=${bl_no}`,
  },
  YMJA: {
    url: () =>
      `https://e-solution.yangming.com/e-service/Track_Trace/track_trace_cargo_tracking.aspx`,
    form: { method: 'GET', fields: mbl_no => ({ str: mbl_no, rdolType: 'BL' }) },
  },
  ONEY: {
    url: (mbl_no, bl_no) =>
      `https://ecomm.one-line.com/one-ecom/manage-shipment/cargo-tracking?trakNoParam=${bl_no}`,
  },
  MEDU: {
    url: () => `https://www.msc.com/en/track-a-shipment`,
  },
  EGLV: {
    url: () => `https://ct.shipmentlink.com/servlet/TDB1_CargoTracking.do`,
    form: {
      method: 'POST',
      fields: (mbl_no, bl_no) => ({ BL: bl_no, TYPE: 'BL', CargoNo: bl_no }),
    },
  },
  OOLU: {
    url: () =>
      `https://www.oocl.com/eng/ourservices/eservices/cargotracking/Pages/cargotracking.aspx`,
  },
  ZIMU: {
    url: mbl_no => `https://www.zim.com/tools/track-a-shipment?consnumber=${mbl_no}`,
  },
  SMLM: {
    url: (mbl_no, bl_no) =>
      `https://esvc.smlines.com/smline/CUP_HOM_3301.do?trakNoTpCdParam=B&trakNoParam=${bl_no}`,
  },
  SMLU: {
    url: (mbl_no, bl_no) =>
      `https://esvc.smlines.com/smline/CUP_HOM_3301.do?trakNoTpCdParam=B&trakNoParam=${bl_no}`,
  },
};

function parseMBLNo(mbl_no: string) {
  const normalized = mbl_no.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return {
    scac: normalized.substring(0, 4),
    mbl_no: normalized,
    bl_no: normalized.substring(4),
  };
}

export function getMBLFromWebsite(mbl_no: string) {
  if (!mbl_no) {
    return null;
  }
  const { scac, mbl_no: normalized, bl_no } = parseMBLNo(mbl_no);
  return CARRIER_SITES[scac]?.url(normalized, bl_no);
}

export function redirectToCarrier(mbl_no: string) {
  if (!mbl_no) {
    return null;
  }
  const { scac, mbl_no: normalized, bl_no } = parseMBLNo(mbl_no);
  const site = CARRIER_SITES[scac];
  if (!site) {
    return undefined;
  }
  const url = site.url(normalized, bl_no);
  // for some carrier, we need to post data to their website
  if (!site.form) {
    return url;
  }
  const form = document.createElement('form');
  form.method = site.form.method;
  form.action = url;
  form.target = '_blank';
  Object.entries(site.form.fields(normalized, bl_no)).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  return;
}

export function getLastClosingDate(monthlyClosingSetting: Record<string, any> | null | undefined) {
  const monthlyClosingDay = get(monthlyClosingSetting, 'day');
  let closingDate;
  if (monthlyClosingDay) {
    closingDate = moment().subtract(1, 'month');
    if (moment().month() !== 0 || !monthlyClosingSetting?.january_auto_lock_day) {
      if (monthlyClosingDay === 'endOfMonth') {
        closingDate = closingDate.endOf('month');
      } else {
        closingDate = closingDate.date(monthlyClosingDay);
      }
      if (
        monthlyClosingSetting?.buffer_days &&
        moment().format('YYYY-MM-DD') <=
          moment(closingDate)
            .add(monthlyClosingSetting?.buffer_days, 'days')
            .format('YYYY-MM-DD')
      ) {
        closingDate = closingDate.subtract(1, 'month');
        if (monthlyClosingDay === 'endOfMonth') {
          closingDate = closingDate.endOf('month');
        }
      }
    } else {
      if (moment().date() <= 6) {
        closingDate = closingDate.subtract(1, 'month').date(monthlyClosingDay);
      } else {
        closingDate = closingDate.endOf('month');
      }
    }
  }
  return closingDate?.format('YYYY-MM-DD');
}

export function buildQueryParams(params: any) {
  const query = new URLSearchParams();
  for (const key in params) {
    if (params[key] !== undefined && params[key] !== null) {
      query.append(key, params[key]);
    }
  }
  return query.toString();
}

export const ExcelDefaultTheadCellStyle = {
  background: 'FF31437c',
  color: 'FFFFFFFF',
  fontSize: 10,
  fontName: 'Helvetica',
  bold: true,
  border: true,
  v: 'center',
  wrapText: true,
};

export const ExcelDefaultTbodyCellStyle = {
  wrapText: true,
  fontName: 'Helvetica',
  fontSize: 10,
};

const EXCEL_INVALID_XML_CHAR_REGEX = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

export function sanitizeExcelXmlString(value: string) {
  return value.replace(EXCEL_INVALID_XML_CHAR_REGEX, '');
}

export function slugifyCompanyName(name: string) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const UNIT_MAPPING = {
  CM: 'CM',
  INCH: 'IN',
  FEET: 'FT',
};

const CONVERSION_RATES = {
  CBM_TO_CFT: 35.3147, // 1立方米 = 35.3147立方英尺

  KG_TO_LB: 2.20462, // 1公斤 = 2.20462磅
  CBM_TO_KG: 1000000 / 6000, // 1CBM = 1,000,000 cm³ / 6000 (air freight volumetric weight divisor)
} as const;

export function getCmFactorUnit(item: any, defaultUnit: string = 'CM') {
  if (item?.dimension_unit) {
    return UNIT_MAPPING[item.dimension_unit as keyof typeof UNIT_MAPPING] || defaultUnit;
  }
  return defaultUnit;
}
export function cmFactor(unit: string) {
  if (unit === 'CM') return 1;
  if (unit === 'INCH' || unit === 'IN') return 16.387064;
  if (unit === 'FEET' || unit === 'FT') return 28316.846592;
  throw Error(`cmFactor get an unexpected unit: ${unit}`);
}

export function convertUnit(value: number, fromUnit: string, toUnit: string): number {
  if (!value) return 0;

  const key = `${fromUnit}_TO_${toUnit}`;

  switch (key) {
    case 'CM_TO_M':
      return value / 100;
    case 'M_TO_CM':
      return value * 100;
    case 'IN_TO_CM':
      return value * 2.54;
    case 'CM_TO_IN':
      return value / 2.54;
    case 'FT_TO_CM':
      return value * 30.48;
    case 'CM_TO_FT':
      return value / 30.48;
    case 'IN_TO_FT':
      return value / 12;
    case 'FT_TO_IN':
      return value * 12;
    case 'M_TO_FT':
      return value * 3.28084;
    case 'FT_TO_M':
      return value / 3.28084;
    case 'M_TO_IN':
      return value * 39.370079;
    case 'IN_TO_M':
      return value / 39.370079;
    case 'CBM_TO_CFT':
      return value * CONVERSION_RATES.CBM_TO_CFT;
    case 'CFT_TO_CBM':
      return value / CONVERSION_RATES.CBM_TO_CFT;
    case 'KG_TO_LB':
      return value * CONVERSION_RATES.KG_TO_LB;
    case 'LB_TO_KG':
      return value / CONVERSION_RATES.KG_TO_LB;
    case 'CBM_TO_KG':
      return value * CONVERSION_RATES.CBM_TO_KG;
    case 'KG_TO_CBM':
      return value / CONVERSION_RATES.CBM_TO_KG;
    default:
      if (fromUnit === toUnit) return value;
      throw new Error(`Unsupported unit conversion: ${fromUnit} to ${toUnit}`);
  }
}

export function calcPCS(item: any) {
  return item.pcs;
}

export function calcKGS(item: any) {
  if (!item) return 0;
  if (item.measure_cbm) {
    return convertUnit(item.measure_cbm, 'CBM', 'KG');
  }
  return (
    (item.length * item.width * item.height * item.pcs * cmFactor(getCmFactorUnit(item))) / 6000
  );
}

export function calcLBS(item: any) {
  if (!item) return 0;
  const kgs = item.measure_cbm ? convertUnit(item.measure_cbm, 'CBM', 'KG') : calcKGS(item);
  return convertUnit(kgs, 'KG', 'LB');
}

export function calcCBM(item: any) {
  return (
    (item.length * item.width * item.height * item.pcs * cmFactor(getCmFactorUnit(item))) / 1000000
  );
}

export function calcCFT(item: any) {
  return calcCBM(item) / 0.02831685;
}

export function calcPCSSum(items: any[], precision?: number) {
  return items.reduce((sum, item) => {
    return sum + parseFloat(calcPCS(item) || 0);
  }, 0);
}

export function calcKGSSum(items: any[], precision?: number) {
  return items.reduce((sum, item) => {
    return sum + calcKGS(item) || 0;
  }, 0);
}

export function calcLBSSum(items: any[], precision?: number) {
  return items.reduce((sum, item) => {
    return sum + calcLBS(item) || 0;
  }, 0);
}

export function calcCBMSum(items: any[], precision?: number) {
  return items.reduce((sum, item) => {
    return sum + (calcCBM(item) || 0);
  }, 0);
}

export function calcCFTSum(items: any[], precision?: number) {
  return items.reduce((sum, item) => {
    return sum + (calcCFT(item) || 0);
  }, 0);
}

export function calcHblRatio(hbl: HBL, hbls: HBL[], shipmentType: string) {
  if (hbls.length <= 1) {
    return 1;
  }
  let key: keyof HBL = 'chargeable_weight';
  if (!shipmentType?.startsWith('air')) {
    key = 'measure';
  }
  const hblWeight = hbl[key] || 0;
  const hblsWeightSum = reduce(hbls, (sum, hbl) => sum.plus(hbl[key] || 0), new Decimal(0));
  if (hblsWeightSum.eq(0)) return 1 / hbls.length;
  return hblWeight / hblsWeightSum.toNumber();
}

export function getContainersForShipment(
  shipment: Shipment,
  { includeEmpty = false }: { includeEmpty?: boolean } = {},
) {
  let containers = [...(shipment.ombl?.containers || [])];
  if (!includeEmpty) {
    containers = containers.filter(container => container.name);
  }
  for (const hbl of shipment.hbls || []) {
    for (const container of hbl.containers || []) {
      if (container.mbl_container) continue;
      if (container.name && !containers.some(c => c.name === container.name)) {
        containers.push(container);
      }
    }
  }
  return containers;
}

export function getShipmentRelationValue(record: Shipment, dataIndex: string) {
  if (!record) return '';
  return getValuesByKey(record, dataIndex).join(',');
}

export function getCommonContactNames(record: Shipment, key: string) {
  return getShipmentRelationValue(record, key + '.name');
}

export function getContactDisplayName(
  contact?: Partial<Pick<Contact, 'name' | 'short_name' | 'code'>> | null,
  { showCode = false, includeShortName = true } = {},
) {
  const baseName =
    contact?.name && contact?.short_name && includeShortName
      ? `${contact.name} (${contact.short_name})`
      : contact?.name || contact?.short_name || '';

  if (showCode && contact?.code) {
    return baseName ? `(${contact.code}) ${baseName}` : `(${contact.code})`;
  }

  return baseName;
}

export function tokenizeMathExpression(expression: string) {
  const tokens = [];
  let currentToken = '';

  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];

    if (char.match(/[0-9.]/)) {
      // Number
      currentToken += char;
    } else if (char.match(/[a-zA-Z]/)) {
      // Variable
      currentToken += char;
    } else if (char.match(/[\+\-\*\/\^\(\)]/)) {
      // Operator or parentheses
      if (currentToken !== '') {
        tokens.push(currentToken);
        currentToken = '';
      }
      tokens.push(char);
    } else if (char === ' ') {
      // Whitespace (ignore)
      if (currentToken !== '') {
        tokens.push(currentToken);
        currentToken = '';
      }
    } else {
      throw new Error(`Invalid character: ${char}`);
    }
  }

  if (currentToken !== '') {
    tokens.push(currentToken);
  }

  return tokens;
}

export function isValidExpression(expression: string) {
  const operators = new Set(['+', '-', '*', '/', '^']);
  const validChars = new Set([...operators, '(', ')', ' ']);
  let openParentheses = 0;
  let prevChar = '';

  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];

    if (char.match(/[a-zA-Z0-9.]/)) {
      // Valid characters: numbers, variables, and decimals
      prevChar = char;
    } else if (validChars.has(char)) {
      if (operators.has(char) && operators.has(prevChar)) {
        return false; // Prevents consecutive operators like ++ or **
      }
      if (char === '(') openParentheses++;
      if (char === ')') openParentheses--;

      if (openParentheses < 0) return false; // More closing parentheses than opening

      prevChar = char;
    } else {
      return false; // Invalid character found
    }
  }

  return openParentheses === 0; // Ensures parentheses are balanced
}

export function getAntdProRequest<T>(path: string, params: any, sort?: any, filter?: any) {
  const { pageSize, current, ...rest } = params;
  return request(path, {
    params: {
      ...rest,
      _limit: pageSize,
      _start: pageSize && current ? pageSize * (current - 1) : undefined,
      _sort: sort
        ? map(sort, (order, key) => `${key}:${order && order.startsWith('desc') ? 'DESC' : 'ASC'}`)
        : undefined,
    },
  });
}

export function getCurrencyEnum(currencies: Currency[]) {
  const options = reduce(
    currencies || [],
    (acc, c) => ({
      ...acc,
      [c.name]: c.name,
    }),
    {},
  );
  // need to make USD and RMB has higher priority
  const sortedOptions = Object.keys(options).sort((a, b) => {
    if (a === 'USD' || a === 'CNY' || a === 'RMB') {
      return -1;
    }
    if (b === 'USD' || b === 'CNY' || b === 'RMB') {
      return 1;
    }
    return 0;
  });
  return sortedOptions.reduce(
    (acc, key) => ({
      ...acc,
      [key]: key,
    }),
    {},
  );
}

// Re-export form utilities for backward compatibility
export { transformDateFieldsToMoment } from './formUtils';
